/**
 * Standardized API Error Response Format
 * Matches the backend error handling structure
 */
export interface ApiErrorResponse {
  message: string;
  timestamp: string;
  requestId: string;
  error: {
    type: ErrorType;
    details?: any;
  };
}

/**
 * Error types returned by the backend API
 */
export enum ErrorType {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  AUTHORIZATION_ERROR = 'AUTHORIZATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  CONFLICT_ERROR = 'CONFLICT_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
}

/**
 * Custom ApiError class that extends Error
 * Provides structured error information from the backend
 */
export class ApiError extends Error {
  public readonly type: ErrorType;
  public readonly details?: any;
  public readonly timestamp: string;
  public readonly requestId: string;
  public readonly statusCode?: number;

  constructor(errorData: ApiErrorResponse, statusCode?: number) {
    super(errorData.message);
    this.name = 'ApiError';
    this.type = errorData.error.type;
    this.details = errorData.error.details;
    this.timestamp = errorData.timestamp;
    this.requestId = errorData.requestId;
    this.statusCode = statusCode;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, ApiError);
    }
  }

  /**
   * Check if an error is an instance of ApiError
   */
  static isApiError(error: any): error is ApiError {
    return error instanceof ApiError;
  }

  /**
   * Create an ApiError from an unknown error
   */
  static fromUnknownError(error: any): ApiError {
    if (ApiError.isApiError(error)) {
      return error;
    }

    // Create a generic error response
    const errorResponse: ApiErrorResponse = {
      message: error?.message || 'An unexpected error occurred',
      timestamp: new Date().toISOString(),
      requestId: '',
      error: {
        type: ErrorType.INTERNAL_ERROR,
        details: { originalError: error },
      },
    };

    return new ApiError(errorResponse);
  }
}

/**
 * Interface for form field errors
 */
export interface FormErrors {
  [key: string]: string;
}


